import { useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAuthContext } from "@covertree/auth";
import { Button, Modal } from "@covertree/ui";
import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";
import LoadingOrValue from "@covertree/ui/loading/LoadingOrValue";
import { Toast } from "@covertree/utils";

import { CognitoUserGroup } from "@enums";
import { Resident } from "@graphql/api";
import { useSetResidentPermanentExclusionMutation } from "@redux/residents/residentsApi";

interface Props {
  isLoading?: boolean;
  resident?: Resident;
}

const AUTHORIZATION_ERROR_MESSAGE =
  "You are not authorized to change permanent exclusion.";
const RESIDENT_UNAVAILABLE_ERROR_MESSAGE =
  "The resident is unavailable. Refresh and try again.";
const ADD_WORKFLOW_ERROR_MESSAGE =
  "Permanent exclusion was added, but the former-resident workflow could not be completed.";

const getErrorMessage = (error: unknown): string => {
  if (Array.isArray(error)) {
    return error.map(getErrorMessage).filter(Boolean).join(" ");
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const errorRecord = error as Record<string, unknown>;

    return [errorRecord.message, errorRecord.data, errorRecord.error]
      .map(getErrorMessage)
      .filter(Boolean)
      .join(" ");
  }

  return "";
};

const showMutationError = (
  error: unknown,
  isAddingPermanentExclusion: boolean
) => {
  const message = getErrorMessage(error).toLowerCase();

  if (
    message.includes("forbidden") ||
    message.includes("not authorized") ||
    message.includes("unauthorized")
  ) {
    Toast.showError(AUTHORIZATION_ERROR_MESSAGE);
    return;
  }

  if (message.includes("not found") || message.includes("does not exist")) {
    Toast.showError(RESIDENT_UNAVAILABLE_ERROR_MESSAGE);
    return;
  }

  if (
    isAddingPermanentExclusion &&
    [
      "workflow",
      "former resident",
      "scheduled action",
      "cognito",
      "coverage",
      "insurance adoption",
      "review process",
    ].some((workflowTerm) => message.includes(workflowTerm))
  ) {
    Toast.showError(ADD_WORKFLOW_ERROR_MESSAGE);
    return;
  }

  Toast.showError(
    `Could not ${
      isAddingPermanentExclusion ? "add" : "remove"
    } permanent exclusion. Please try again.`
  );
};

export default function PermanentExclusionDetails({
  isLoading,
  resident,
}: Props) {
  const { hasGroup } = useAuthContext();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [setResidentPermanentExclusion, { isLoading: isMutationLoading }] =
    useSetResidentPermanentExclusionMutation();

  const canChangePermanentExclusion = hasGroup([
    CognitoUserGroup.CoverTreeEmployee,
    CognitoUserGroup.CoverTreeExecutive,
  ]);
  const isPermanentlyExcluded = resident?.isPermanentlyExcluded ?? false;
  const isAddingPermanentExclusion = !isPermanentlyExcluded;
  const isInteractionDisabled = isLoading || isMutationLoading;
  const actionLabel = isAddingPermanentExclusion
    ? "Add permanent exclusion"
    : "Remove permanent exclusion";

  const closeConfirmation = () => {
    if (!isInteractionDisabled) {
      setIsConfirmationOpen(false);
    }
  };

  const confirmPermanentExclusionChange = async () => {
    if (!resident) {
      return;
    }

    try {
      await setResidentPermanentExclusion({
        id: resident.id,
        isPermanentlyExcluded: isAddingPermanentExclusion,
      }).unwrap();
      setIsConfirmationOpen(false);
      Toast.showSuccess(
        isAddingPermanentExclusion
          ? "Permanent exclusion added successfully."
          : "Permanent exclusion removed successfully."
      );
    } catch (error) {
      showMutationError(error, isAddingPermanentExclusion);
    }
  };

  return (
    <>
      <DisplayPropertiesCard
        properties={[
          {
            label: "Permanent exclusion",
            gridSize: 8,
            inlineButton:
              canChangePermanentExclusion && resident ? (
                <Button
                  size="small"
                  disabled={isInteractionDisabled}
                  onClick={() => setIsConfirmationOpen(true)}>
                  {actionLabel}
                </Button>
              ) : null,
            value: (
              <LoadingOrValue
                loading={!resident && isLoading}
                value={
                  resident
                    ? isPermanentlyExcluded
                      ? "Permanently excluded"
                      : "Not permanently excluded"
                    : undefined
                }
                height={28}
                width={200}
              />
            ),
          },
        ]}></DisplayPropertiesCard>

      {canChangePermanentExclusion && resident ? (
        <Modal
          open={isConfirmationOpen}
          disableClose={isInteractionDisabled}
          title={`Confirm ${
            isAddingPermanentExclusion ? "Adding" : "Removing"
          } Permanent Exclusion`}
          onClose={closeConfirmation}
          maxWidth="sm">
          <Stack spacing={2}>
            <Typography>
              <strong>Resident:</strong> {resident.firstName}{" "}
              {resident.lastName}
            </Typography>
            <Typography>
              <strong>Resident ID:</strong> {resident.residentId}
            </Typography>
            <Typography>
              <strong>Requested action:</strong> {actionLabel}
            </Typography>

            {isAddingPermanentExclusion ? (
              <>
                <Typography>
                  Adding permanent exclusion applies the existing
                  former-resident workflow:
                </Typography>
                <Box component="ul" my={0} pl={3}>
                  <Typography component="li">
                    The resident becomes a former resident.
                  </Typography>
                  <Typography component="li">
                    The active review process receives existing former-resident
                    cleanup.
                  </Typography>
                  <Typography component="li">
                    Resident scheduled actions receive existing former-resident
                    cleanup.
                  </Typography>
                  <Typography component="li">
                    The resident&apos;s Cognito account is deleted.
                  </Typography>
                  <Typography component="li">
                    The insurance adoption status becomes Excluded.
                  </Typography>
                  <Typography component="li">
                    Existing former-resident coverage actions apply.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Typography>
                  Only the permanent exclusion flag will be cleared.
                </Typography>
                <Typography>
                  No other resident data will be restored or changed.
                </Typography>
              </>
            )}

            <Box display="flex" justifyContent="flex-end" pt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmPermanentExclusionChange}
                disabled={isInteractionDisabled}
                loading={isMutationLoading}>
                Confirm
              </Button>
              <Button
                variant="outlined"
                disabled={isInteractionDisabled}
                onClick={closeConfirmation}
                sx={{ ml: 2 }}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </Modal>
      ) : null}
    </>
  );
}
